import {Record, List} from 'immutable';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import Image from 'web-app/react/entities/common/image';

class Organization extends Record<{
    organizationId: string;
    title: string;
}>({
    organizationId: '',
    title: '',
}) {}

interface ContactPerson {
    firstName: string;
    fullName: string;
    lastName: string;
    middleName: string;
}

interface Address {
    street: string;
    zip: string;
    city: string;
    country: string;
    state: string;
}

interface FamlyPay {
    /** Indicator of whether feature for this experiment is enabled and user has permission to manage Famly Pay */
    shouldShowDebtCollectionFamlyPayBanner: boolean;
    /** Indicator of whether user has permission to manage Famly Pay */
    hasPermissionToManageFamlyPay: boolean;
    /** Status of Famly Pay */
    status: {
        /** Indicator of whether Famly Pay features are enabled */
        enabled: boolean;
        /** Indicator of whether the country of site is supported by Famly Pay */
        eligible: boolean;
        /** Indicator of whether the Famly Pay signup process has started */
        started: boolean;
        /** Indicator of whether the site is Stripe verified */
        verified: boolean;
        /** Indicator of whether automatic payments are enabled by site */
        automaticPaymentsEnabled: boolean;
    };
}

export interface InstitutionType {
    institutionId: string;
    organizationId?: string;
    organization?: Organization;
    features: string[];
    behaviors: BehaviorsCollection;
    ageGroupBehaviors: BehaviorsCollection;
    openingHoursBehaviors: BehaviorsCollection;
    country?: string;
    locale?: string;
    image: any;
    title?: string;
    identificationNumber?: string;
    timezone?: string;
    stats: any;
    fetchingGroups: boolean;
    groupIds: List<string>;
    allGroups: List<any>;
    employeeIds: List<string>;
    ageGroupIds: List<string>;
    tagIds: List<string>;
    fetchingAgeGroups: boolean;
    updatingAgeGroups: boolean;
    openingHours: List<any>;
    fetchingOpeningHours: boolean;
    updatingOpeningHours: boolean;
    usePincodeCheckin: boolean;
    shortname: string;
    didFetchGroups: boolean;
    address: Address | null;
    contactPerson: ContactPerson | null;
    ean: string | null;
    vat: string | null;
    phone: string | null;
    email: string | null;
    famlyId: string;
    /**
     * Object with data related to Famly Pay experiments.
     * Coming from REST API - InstitutionController.php
     */
    famlyPay?: FamlyPay;
    /**
     * Growth experiment: Banner on invoicing page.
     * Boolean flag to frontend indicating whether site
     * has Famly Pay enabled (is eligible) but haven't set it up.
     * Coming from REST API - InstitutionController.php
     */
    showSetUpFamlyPayBanner?: boolean;
}

export class Institution extends Record<InstitutionType>({
    institutionId: '',
    organizationId: undefined,
    organization: undefined,
    features: [], // Should be removed once all feature flags are gone from the product
    behaviors: new BehaviorsCollection([]),
    ageGroupBehaviors: new BehaviorsCollection([]),
    openingHoursBehaviors: new BehaviorsCollection([]),
    country: undefined,
    locale: undefined,
    image: null,
    title: undefined,
    timezone: undefined,
    stats: null,
    fetchingGroups: false,
    groupIds: List(),
    allGroups: List(),
    employeeIds: List(),
    ageGroupIds: List(),
    tagIds: List(),
    fetchingAgeGroups: false,
    updatingAgeGroups: false,
    openingHours: List(),
    fetchingOpeningHours: false,
    updatingOpeningHours: false,
    usePincodeCheckin: false,
    shortname: '',
    didFetchGroups: false,
    address: null,
    contactPerson: null,
    ean: null,
    vat: null,
    identificationNumber: undefined,
    phone: null,
    email: null,
    famlyId: '',
    famlyPay: {
        shouldShowDebtCollectionFamlyPayBanner: false,
        hasPermissionToManageFamlyPay: false,
        status: {
            enabled: false,
            eligible: false,
            started: false,
            verified: false,
            automaticPaymentsEnabled: false,
        },
    },
    showSetUpFamlyPayBanner: false,
}) {}

function fromAPI(data: any) {
    const {behaviors, organization, image, ...otherData} = data;

    const thisOrganization = organization ? new Organization(organization) : undefined;

    return new Institution({
        behaviors: new BehaviorsCollection(behaviors),
        organization: thisOrganization,
        image: Image.fromAPI(image),
        ...otherData,
    });
}

export default {
    record: Institution,
    default: new Institution(),
    fromAPI,
};
